/**
 * Reading a folder the user picked in the page, for the browser build (its core has no disk):
 * the files are read here and handed to the core with `analyze.scan_files`.
 *
 * Two ways in, depending on the browser: a folder handle from `showDirectoryPicker()` (Chrome,
 * Edge: can be read again later for a re-scan), or the file list of an
 * `<input type="file" webkitdirectory>` (every browser, read once). Both skip the same folders
 * as the core's walk, early, so `node_modules` is never read. A game engine's packages are sent
 * by their beginning in base64, and only those holding a Blueprint.
 */

import { toBase64 } from "./base64";

/**
 * Folders never worth reading. **Same list as `SKIPPED_DIRS` in
 * `libs/da-analyze/src/project.rs`** (a Rust test checks it).
 */
export const SKIPPED_DIRS = [
  "target", "node_modules", "dist", "build", "out", "bin", "obj", ".git", ".hg", ".svn", "__pycache__", ".venv", "venv",
  ".tox", ".mypy_cache", ".pytest_cache", ".gradle", ".idea", ".vscode", ".next", ".nuxt", "coverage", "vendor",
  // Game engines' generated folders (Unreal, Unity).
  "Intermediate", "Binaries", "Saved", "DerivedDataCache", "Library", "Temp",
];

/** Files bigger than this are not read (the core skips them too). */
export const MAX_FILE_BYTES = 1_000_000;

/**
 * Files read by their beginning, whatever their size (a game engine's packages, whose tables come
 * first). **Same list as `READ_BY_HEAD` in `libs/da-analyze/src/project.rs`** (a Rust test checks it).
 */
export const READ_BY_HEAD = ["uasset"];

/** The most of such a file sent to the core (`MAX_HEAD_BYTES` there). */
export const MAX_HEAD_BYTES = 4 << 20;

/** How much of a package is looked at first: only a Blueprint's names a generated class. */
const PEEK_BYTES = 256 << 10;

/** The most files read from one folder. */
export const MAX_FILES = 20_000;

/** A file read from the picked folder: its path inside the folder and its text (or, for a package, its beginning in base64). */
export interface PickedFile {
  path: string;
  text: string;
  base64?: string;
}

/** A picked folder, read: its name and its files. */
export interface PickedFolder {
  name: string;
  files: PickedFile[];
}

/** Whether a folder called `name` is skipped (build output, hidden tool folders...). */
export function skipsFolder(name: string): boolean {
  return SKIPPED_DIRS.includes(name) || (name.startsWith(".") && name !== ".");
}

/** Whether a file's text looks binary (a zero byte near the start): such files are dropped. */
export function looksBinary(text: string): boolean {
  return text.slice(0, 1024).includes("\u0000");
}

/** Whether a file named `name` is read by its beginning ([`READ_BY_HEAD`]). */
export function readByHead(name: string): boolean {
  const dot = name.lastIndexOf(".");
  return dot > 0 && READ_BY_HEAD.includes(name.slice(dot + 1));
}

/** Whether `bytes` hold the ASCII text `word`. */
export function holdsWord(bytes: Uint8Array, word: string): boolean {
  const codes = Array.from(word, (c) => c.charCodeAt(0));
  outer: for (let i = 0; i + codes.length <= bytes.length; i++) {
    for (let j = 0; j < codes.length; j++) if (bytes[i + j] !== codes[j]) continue outer;
    return true;
  }
  return false;
}

/** A package's beginning in base64 when it holds a Blueprint (it names a generated class), else `null`: meshes, textures and sounds are never sent. */
export async function packageHead(file: Blob): Promise<string | null> {
  const peek = new Uint8Array(await file.slice(0, PEEK_BYTES).arrayBuffer());
  if (!holdsWord(peek, "GeneratedClass")) return null;
  const head = file.size <= PEEK_BYTES ? peek : new Uint8Array(await file.slice(0, MAX_HEAD_BYTES).arrayBuffer());
  return toBase64(head);
}

/** The file to hand over for `file` named `name` at `path`, or `null` when it is not worth reading. */
async function picked(path: string, name: string, file: File): Promise<PickedFile | null> {
  if (readByHead(name)) {
    const base64 = await packageHead(file);
    return base64 === null ? null : { path, text: "", base64 };
  }
  if (file.size > MAX_FILE_BYTES) return null;
  const text = await file.text();
  return looksBinary(text) ? null : { path, text };
}

/** The part of the browser's `FileSystemDirectoryHandle` used here. */
export interface DirectoryHandle {
  kind: "directory";
  name: string;
  values(): AsyncIterable<DirectoryHandle | FileHandle>;
}

/** The part of the browser's `FileSystemFileHandle` used here. */
export interface FileHandle {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
}

/** Reads every useful file below a folder handle (`showDirectoryPicker()`). */
export async function readHandle(root: DirectoryHandle): Promise<PickedFolder> {
  const files: PickedFile[] = [];
  /** Reads `dir`, whose path inside the root is `prefix`. */
  const walk = async (dir: DirectoryHandle, prefix: string): Promise<void> => {
    for await (const entry of dir.values()) {
      if (files.length >= MAX_FILES) return;
      const path = prefix + entry.name;
      if (entry.kind === "directory") {
        if (!skipsFolder(entry.name)) await walk(entry, path + "/");
      } else if (entry.name === ".gitignore" || !entry.name.startsWith(".")) {
        const found = await picked(path, entry.name, await entry.getFile());
        if (found) files.push(found);
      }
    }
  };
  await walk(root, "");
  return { name: root.name, files };
}

/** Reads the files of an `<input webkitdirectory>` (their `webkitRelativePath` starts with the folder's name). */
export async function readFileList(list: ArrayLike<File>): Promise<PickedFolder> {
  const files: PickedFile[] = [];
  let name = "";
  for (const file of Array.from(list)) {
    if (files.length >= MAX_FILES) break;
    const parts = (file.webkitRelativePath || file.name).split("/");
    name ||= parts.length > 1 ? (parts[0] ?? "") : "";
    const inside = parts.length > 1 ? parts.slice(1) : parts;
    const dirs = inside.slice(0, -1);
    const fileName = inside[inside.length - 1] ?? "";
    if (dirs.some(skipsFolder) || (fileName.startsWith(".") && fileName !== ".gitignore")) continue;
    const found = await picked(inside.join("/"), fileName, file);
    if (found) files.push(found);
  }
  return { name: name || "project", files };
}
